import { KeyboardEvent, useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { Tooltip } from '@mui/material';
import { useIntercom } from 'react-use-intercom';
import {
  IAssistantEvent,
  IAssistantMessage,
  IAssistantQuestion,
} from 'shared';
import {
  CheckIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';

import { Button } from '../../../common/Atoms/Button';
import { Modal } from '../../../common/Atoms/Modal';
import { TextArea } from '../../../common/Atoms/TextArea';
import { Paragraph } from '../../../common/Atoms/Typography/Paragraph';
import { classNames } from '../../../common/lib/classNames';
import { mixpanelService } from '../../../common/lib/mixpanel';
import { useGetSelectedOrganisation } from '../../hooks/useGetSelectedOrganisation';
import { useModals } from '../../hooks/useModals';
import { useReportAssistantMessageMutation } from '../../services/api/assistantApi/assistant';

import { AssistantMarkdown } from './AssistantMarkdown';
import { EmailDraftCard } from './EmailDraftCard';
import { ProposalCard } from './ProposalCard';

interface AssistantMessageProps {
  message: IAssistantMessage;
  onApply: (message: IAssistantMessage) => Promise<void>;
  onDismiss: (message: IAssistantMessage) => Promise<void>;
  /** Sends answers to the clarify questions; only offered on the latest message of the current chat */
  onAnswer?: (content: string) => Promise<void>;
  /** What the user replied to this clarify, so earlier questions still show which option was picked */
  answer?: string;
  busy: boolean;
  readOnly?: string;
}

interface Step {
  key: string;
  label: string;
  finished: boolean;
  warning?: boolean;
}

/** Pairs tool started/finished events into one line each; redrafts and retries are their own line */
export function toSteps(events: IAssistantEvent[]): Step[] {
  const steps: Step[] = [];

  for (const event of events) {
    if (event.type === `tool_finished`) {
      const open = [...steps].reverse().find(step => step.key === `tool:${event.tool}` && !step.finished);

      if (open) {
        open.finished = true;
      }

      continue;
    }

    steps.push({
      key: event.tool ? `tool:${event.tool}` : `${event.type}:${event.id}`,
      label: event.label,
      finished: event.type !== `tool_started`,
      warning: event.type === `retry`,
    });
  }

  return steps;
}

const OPTION_CLASS = `rounded-full border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-900 transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60`;

/** A multi-question answer is one "question\nanswer" block per question; a single question's answer is sent as is */
export function formatAnswers(questions: IAssistantQuestion[], answers: string[]): string {
  return questions.length === 1
    ? answers[0].trim()
    : questions.map((question, index) => `${question.question}\n${answers[index].trim()}`).join(`\n\n`);
}

/** Reads the answers back out of the user's reply, so earlier questions still show what was picked */
export function parseAnswers(questions: IAssistantQuestion[], reply?: string): (string | undefined)[] {
  if (reply === undefined || questions.length === 1) {
    return [reply];
  }

  const blocks = new Map(reply.split(`\n\n`).map(block => {
    const [question, ...answer] = block.split(`\n`);

    return [question, answer.join(`\n`)];
  }));

  return questions.map(question => blocks.get(question.question));
}

interface QuestionProps {
  question: IAssistantQuestion;
  /** The picked or typed answer */
  selected?: string;
  busy: boolean;
  /** Without it (an earlier question) the pills are read-only */
  onChoose?: (answer: string) => void;
  /** Single question: the free-text editor sends straight away instead of only recording the answer */
  onSend?: (answer: string) => Promise<void>;
}

/** One question's answers plus an "other" pill that opens an inline editor */
function Question({ question, selected, busy, onChoose, onSend }: QuestionProps) {
  const [custom, setCustom] = useState<string | null>(null);
  const active = !!onChoose;
  const otherPicked = !!selected && !question.options.includes(selected);

  async function submit() {
    if (custom?.trim() && onSend) {
      await onSend(custom.trim());
      setCustom(null);
    }
  }

  async function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (onSend && event.key === `Enter` && !event.shiftKey) {
      event.preventDefault();
      await submit();
    }
  }

  return (
    <div className={ `flex w-full flex-col gap-2` }>
      { question.question && <p className={ `text-sm font-medium text-slate-800` }>{ question.question }</p> }
      <div className={ `flex flex-wrap items-center gap-2` }>
        {
          question.options.map(option => (
            <button
              key={ option }
              type={ `button` }
              disabled={ busy || !active }
              onClick={ () => {
                setCustom(null);
                onChoose?.(option);
              } }
              className={ classNames(OPTION_CLASS, !active && `disabled:opacity-100`, selected === option && `border-indigo-600 bg-indigo-600 text-white`) }
            >
              { option }
            </button>
          ))
        }
        <button
          type={ `button` }
          disabled={ busy || !active }
          onClick={ () => {
            if (custom !== null) {
              setCustom(null);

              return;
            }

            // Reopening keeps a typed answer; replacing a picked option clears it until something is typed
            setCustom(otherPicked ? selected : ``);

            if (!onSend && !otherPicked) {
              onChoose?.(``);
            }
          } }
          className={ classNames(OPTION_CLASS, `border-dashed`, custom !== null && `bg-indigo-50`, !active && `disabled:opacity-100`, otherPicked && `border-solid border-indigo-600 bg-indigo-600 text-white`) }
        >
          { question.otherOptionLabel || `Something else` }
        </button>
      </div>
      {
        custom !== null &&
        <div className={ `flex items-end gap-2` }>
          <textarea
            autoFocus
            rows={ 2 }
            value={ custom }
            disabled={ busy }
            onChange={ event => {
              setCustom(event.target.value);

              if (!onSend) {
                onChoose?.(event.target.value);
              }
            } }
            onKeyDown={ onKeyDown }
            placeholder={ `Describe what you have in mind...` }
            className={ `block w-full resize-none rounded-lg border border-indigo-300 px-3 py-2 text-sm text-slate-800 shadow-xs focus:border-paidniceGreen-500 focus:outline-hidden focus:ring-2 focus:ring-paidniceGreen-500/20 disabled:bg-slate-100` }
          />
          {
            onSend &&
            <Button
              size={ `sm` }
              disabled={ busy || !custom.trim() }
              onClick={ submit }
            >
              { `Send` }
            </Button>
          }
        </div>
      }
    </div>
  );
}

/**
 * Clarify questions. One question sends as soon as an answer is picked; several are answered
 * together and sent with one button. Without `onAnswer` (an earlier message) they are read-only
 * and show what `reply` answered.
 */
function Questions({ questions, busy, onAnswer, reply }: { questions: IAssistantQuestion[]; busy: boolean; onAnswer?: (content: string) => Promise<void>; reply?: string }) {
  const [answers, setAnswers] = useState<string[]>(() => questions.map(() => ``));
  const single = questions.length === 1;
  const previous = parseAnswers(questions, reply);
  const complete = answers.every(answer => answer.trim());

  return (
    <div className={ `flex w-full flex-col gap-4` }>
      {
        questions.map((question, index) => (
          <Question
            key={ index }
            question={ question }
            selected={ onAnswer ? answers[index] : previous[index] }
            busy={ busy }
            onChoose={ onAnswer && (single ? onAnswer : answer => setAnswers(current => current.map((value, i) => i === index ? answer : value))) }
            onSend={ single ? onAnswer : undefined }
          />
        ))
      }
      {
        onAnswer && !single &&
        <div>
          <Button
            size={ `sm` }
            disabled={ busy || !complete }
            onClick={ () => onAnswer(formatAnswers(questions, answers)) }
          >
            { `Send answers` }
          </Button>
        </div>
      }
    </div>
  );
}

const SUPPORT_CLASS = `inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 transition hover:bg-amber-100`;

/** Ways to reach a person, shown when the assistant cannot help or the turn failed */
function SupportOptions() {
  const { show: showIntercom } = useIntercom();
  const { show: showModal } = useModals();

  return (
    <div className={ `flex flex-wrap items-center gap-2` }>
      <Paragraph
        size={ `xs` }
        variant={ `secondary` }
        className={ `mr-1` }
      >
        { `Need a person?` }
      </Paragraph>
      <button
        type={ `button` }
        className={ SUPPORT_CLASS }
        onClick={ () => showIntercom() }
      >
        <ChatBubbleLeftRightIcon className={ `h-3.5 w-3.5` } />
        { `Chat with support` }
      </button>
      <a
        href={ `mailto:hello@paidnice.com` }
        className={ SUPPORT_CLASS }
      >
        <EnvelopeIcon className={ `h-3.5 w-3.5` } />
        { `Email us` }
      </a>
      <button
        type={ `button` }
        className={ SUPPORT_CLASS }
        onClick={ () => {
          showModal(`call`);
          mixpanelService.demoCallClicked();
        } }
      >
        <PhoneIcon className={ `h-3.5 w-3.5` } />
        { `Book a call` }
      </button>
    </div>
  );
}

/** Flags a reply to the Paidnice team; `reported` only lasts for the session as reports are not stored */
function ReportButton({ message }: { message: IAssistantMessage }) {
  const organisationId = useGetSelectedOrganisation()?.id;
  const [report, { isLoading }] = useReportAssistantMessageMutation();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(``);
  const [reported, setReported] = useState(false);

  async function submit() {
    if (!organisationId) {
      return;
    }

    try {
      await report({ organisationId, messageId: message.id, notes: notes.trim() || undefined }).unwrap();
      setReported(true);
      setOpen(false);
      toast.success(`Thanks, your report has been sent to the Paidnice team.`);
    }
    catch {
      toast.error(`Could not send your report. Please try again.`);
    }
  }

  return (
    <>
      <Tooltip title={ reported ? `Reported` : `Report this response` }>
        <span>
          <button
            type={ `button` }
            aria-label={ `Report this response` }
            disabled={ reported }
            onClick={ () => setOpen(true) }
            className={ classNames(`rounded p-0.5 transition`, reported ? `text-amber-600` : `text-slate-400 hover:text-slate-600`) }
          >
            <FlagIcon className={ `h-3.5 w-3.5` } />
          </button>
        </span>
      </Tooltip>
      <Modal
        title={ `Report this conversation` }
        open={ open }
        setOpen={ setOpen }
      >
        <Paragraph variant={ `secondary` }>
          { `Let us know if the assistant has gone off track, misunderstood you, or is giving low quality or incorrect suggestions. The Paidnice team will review this conversation to help improve it.` }
        </Paragraph>
        <TextArea
          className={ `mt-4` }
          label={ `Notes (optional)` }
          name={ `notes` }
          value={ notes }
          onChange={ event => setNotes(String(event.value).slice(0, 2000)) }
          placeholder={ `What went wrong, or what did you expect instead?` }
        />
        <div className={ `mt-4 flex justify-end gap-2` }>
          <Button
            color={ `gray` }
            onClick={ () => setOpen(false) }
          >
            { `Cancel` }
          </Button>
          <Button
            loading={ isLoading }
            disabled={ isLoading }
            onClick={ submit }
          >
            { `Send report` }
          </Button>
        </div>
      </Modal>
    </>
  );
}

function Pulse() {
  return (
    <span className={ `inline-flex items-center gap-1` }>
      <span className={ `h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400` } />
      <span className={ `h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400 [animation-delay:120ms]` } />
      <span className={ `h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400 [animation-delay:240ms]` } />
    </span>
  );
}

/** Progress list for a running turn (also used by the dashboard suggestions widget); `idleLabels` is what shows while no tool is in flight */
export function Steps({ steps, running, idleLabels = [`Reading your request`, `Working out the answer`] }: { steps: Step[]; running: boolean; idleLabels?: [string, string] }) {
  return (
    <ul className={ `space-y-1 text-xs text-slate-600` }>
      {
        steps.map((step, index) => (
          <li
            key={ `${step.key}-${index}` }
            className={ `flex items-center gap-2` }
          >
            {
              step.warning
                ? <ExclamationTriangleIcon className={ `h-3.5 w-3.5 text-amber-500` } />
                : step.finished
                  ? <CheckIcon className={ `h-3.5 w-3.5 text-paidniceGreen-600` } />
                  : <Pulse />
            }
            <span className={ step.warning ? `text-amber-700` : undefined }>{ step.label }</span>
          </li>
        ))
      }
      {
        running && (!steps.length || steps.every(step => step.finished)) &&
        <li className={ `flex items-center gap-2` }>
          <Pulse />
          <span>{ steps.length ? idleLabels[1] : idleLabels[0] }</span>
        </li>
      }
    </ul>
  );
}

export function AssistantMessage({ message, onApply, onDismiss, onAnswer, answer, busy, readOnly }: AssistantMessageProps) {
  const isUser = message.role === `user`;
  const running = message.status === `running`;
  const failed = message.status === `failed`;
  const steps = toSteps(message.events);
  const createdAt = message.createdAt ? format(new Date(message.createdAt), `PPp`) : ``;

  return (
    <div className={ classNames(`flex`, isUser ? `justify-end` : `justify-start`) }>
      <div className={ classNames(`flex flex-col gap-2`, isUser ? `max-w-[85%] items-end` : `w-full max-w-[95%] items-start`) }>
        {
          isUser && message.context &&
          <span className={ `inline-flex items-center gap-1 text-[11px] text-slate-500` }>
            <DocumentTextIcon className={ `h-3.5 w-3.5` } />
            { `Sent from ${message.context.label}` }
          </span>
        }
        <div
          className={ classNames(
            `rounded-2xl px-4 py-3 text-sm shadow-xs`,
            isUser && `whitespace-pre-wrap`,
            isUser
              ? `bg-paidniceGreen-600 text-white`
              : failed
                ? `border border-red-200 bg-red-50 text-red-900`
                : message.kind === `cannot_help`
                  ? `border border-amber-200 bg-amber-50 text-amber-900`
                  : `border border-slate-200 bg-white text-slate-800`,
          ) }
        >
          {
            running
              ? <Steps
                steps={ steps }
                running
              />
              : isUser || failed
                ? message.content
                : <AssistantMarkdown content={ message.content } />
          }
          {
            !running && steps.length > 0 &&
            <details className={ `mt-2` }>
              <summary className={ `cursor-pointer text-xs text-slate-500` }>{ `${steps.length} step${steps.length === 1 ? `` : `s`}` }</summary>
              <div className={ `mt-1` }>
                <Steps
                  steps={ steps }
                  running={ false }
                />
              </div>
            </details>
          }
        </div>
        {
          message.kind === `clarify` && !!message.questions?.length &&
          <Questions
            questions={ message.questions }
            busy={ busy }
            onAnswer={ onAnswer }
            reply={ answer }
          />
        }
        { !running && (failed || message.kind === `cannot_help`) && <SupportOptions /> }
        {
          message.proposal &&
          <ProposalCard
            message={ message }
            busy={ busy }
            readOnly={ readOnly }
            onApply={ () => onApply(message) }
            onDismiss={ () => onDismiss(message) }
          />
        }
        {
          message.emailDraft &&
          <EmailDraftCard
            message={ message }
            busy={ busy }
            readOnly={ readOnly }
            onDismiss={ () => onDismiss(message) }
          />
        }
        {
          !running &&
          <div className={ `flex items-center gap-2` }>
            <Paragraph
              size={ `xs` }
              variant={ `secondary` }
            >
              { createdAt }
              { message.costUsd !== undefined && ` · $${message.costUsd.toFixed(4)}` }
            </Paragraph>
            { !isUser && <ReportButton message={ message } /> }
          </div>
        }
      </div>
    </div>
  );
}
